import { getState } from '/bb/state.js';
import { welfordAdd } from '/bb/aggregators/welford.js';
import { resolveSection } from '/bb/identifier.js';
import { ensureCurrentPage, getOrCreateSection } from '/bb/helpers.js';
import { clampUnitInterval } from '/bb/normalizers.js';

const GESTURE_GAP_MS = 300;
const MOBILE_ZOOM_TOUCH_GRACE_MS = 1000;
const MOBILE_ZOOM_SCALE_EPSILON = 0.01;
const MOBILE_ZOOM_SAMPLE_GRID_SIZE = 5;
const MOBILE_ZOOM_MIN_HITS = 3;
const MOBILE_ZOOM_CLEAR_LEAD_HITS = 3;
const MOBILE_ZOOM_AMBIGUITY_HITS = 2;

let scrollLast = new WeakMap();
let zoomLast = new WeakMap();
let lastSessionScrollAt = 0;
let mobileZoom = createMobileZoomState();

function createMobileZoomState() {
  return {
    lastScale: null,
    touchSection: null,
    touchSectionAt: 0,
    gestureActive: false,
    gestureCounted: false,
  };
}

function scrollerOf(e) {
  const target = e.target;
  if (target === document || target === document.documentElement || target === document.body || !target.nodeType) {
    return document.scrollingElement || document.documentElement;
  }
  if (target.scrollHeight > target.clientHeight) return target;
  return document.scrollingElement || document.documentElement;
}

function updateBucket(bucket, dy, dt, startsGesture) {
  if (startsGesture) bucket.count += 1;
  if (dt <= 0) return;
  bucket.sumDist += Math.abs(dy);
  if (startsGesture) return;
  bucket.sumTime += dt;
  welfordAdd(bucket.speed, Math.abs(dy) / dt);
}

function scrollHandler(e) {
  const s = getState();
  const t = Date.now();
  const scroller = scrollerOf(e);
  if (!scroller) return;
  const prev = scrollLast.get(scroller);
  const top = typeof scroller.scrollTop === 'number' ? scroller.scrollTop : 0;
  const dy = prev ? top - prev.top : 0;
  const dt = prev ? t - prev.t : 0;
  const startsScrollerGesture = !prev || dt > GESTURE_GAP_MS;
  const startsSessionGesture = lastSessionScrollAt === 0 || t - lastSessionScrollAt > GESTURE_GAP_MS;
  lastSessionScrollAt = t;
  scrollLast.set(scroller, { top, t });

  const max = Math.max(0, scroller.scrollHeight - scroller.clientHeight);
  // scrollTop may be subpixel-precise while scrollHeight/clientHeight are
  // rounded. At the bottom of a mobile page their ratio can therefore exceed
  // one by a fraction of a pixel.
  const depth = clampUnitInterval(max > 0 ? top / max : 0);

  const bucket = s.session.pointer.scroll;
  updateBucket(bucket, dy, dt, startsSessionGesture);
  if (depth > bucket.depthMax) bucket.depthMax = depth;

  const section = resolveSection(scroller === document.scrollingElement ? e.target : scroller);
  if (section) {
    const pageKey = ensureCurrentPage();
    const sec = getOrCreateSection(pageKey, section.id, section.sectionCategories);
    updateBucket(sec.scroll, dy, dt, startsScrollerGesture);
    const secMax =
      section.element && section.element.scrollHeight > section.element.clientHeight
        ? section.element.scrollHeight - section.element.clientHeight
        : 0;
    const secTop = section.element ? section.element.scrollTop : 0;
    const secDepth = clampUnitInterval(secMax > 0 ? secTop / secMax : depth);
    if (secDepth > sec.scroll.depthMax) sec.scroll.depthMax = secDepth;
    sec.runtime.lastActivityAt = t;
  }
}

function wheelHandler(e) {
  if (e.ctrlKey || e.metaKey) {
    const sec = resolveSection(e.target);
    if (sec) {
      const t = Date.now();
      const zoomTarget = sec.element || e.target;
      const lastAt = zoomLast.get(zoomTarget) || 0;
      zoomLast.set(zoomTarget, t);
      if (lastAt && t - lastAt <= GESTURE_GAP_MS) return;
      const pageKey = ensureCurrentPage();
      const s = getOrCreateSection(pageKey, sec.id, sec.sectionCategories);
      s.zoom_count += 1;
    }
  }
}

function startMobileZoomGesture(target) {
  if (mobileZoom.gestureActive) return;

  const section = resolveSection(target);
  mobileZoom.touchSection = section ? { id: section.id, sectionCategories: section.sectionCategories } : null;
  mobileZoom.touchSectionAt = Date.now();
  mobileZoom.gestureActive = true;
  mobileZoom.gestureCounted = false;
}

function touchStartHandler(e) {
  startMobileZoomGesture(e.target);
}

function touchEndHandler(e) {
  if (!mobileZoom.gestureActive || (e.touches && e.touches.length > 0)) return;
  mobileZoom.gestureActive = false;
}

function pointerDownHandler(e) {
  if (!e || e.pointerType !== 'touch') return;
  startMobileZoomGesture(e.target);
}

function pointerUpHandler(e) {
  if (!e || e.pointerType !== 'touch' || !mobileZoom.gestureActive) return;
  mobileZoom.gestureActive = false;
}

function selectMobileZoomSection(t) {
  if (typeof document === 'undefined' || typeof document.elementFromPoint !== 'function') return null;
  const viewport = window.visualViewport;
  const width = viewport ? viewport.width : window.innerWidth;
  const height = viewport ? viewport.height : window.innerHeight;
  if (!(width > 0) || !(height > 0)) return null;

  const sections = new Map();
  for (let row = 0; row < MOBILE_ZOOM_SAMPLE_GRID_SIZE; row += 1) {
    for (let column = 0; column < MOBILE_ZOOM_SAMPLE_GRID_SIZE; column += 1) {
      const x = ((column + 0.5) * width) / MOBILE_ZOOM_SAMPLE_GRID_SIZE;
      const y = ((row + 0.5) * height) / MOBILE_ZOOM_SAMPLE_GRID_SIZE;
      const section = resolveSection(document.elementFromPoint(x, y));
      if (!section) continue;
      const current = sections.get(section.id);
      if (current) current.hits += 1;
      else sections.set(section.id, { ...section, hits: 1 });
    }
  }

  const candidates = Array.from(sections.values())
    .filter((section) => section.hits >= MOBILE_ZOOM_MIN_HITS)
    .sort((a, b) => b.hits - a.hits);
  if (candidates.length === 0) return null;

  const leader = candidates[0];
  const runnerUp = candidates[1];
  if (!runnerUp || leader.hits - runnerUp.hits >= MOBILE_ZOOM_CLEAR_LEAD_HITS) return leader;

  const ambiguousCandidates = candidates.filter((section) => leader.hits - section.hits <= MOBILE_ZOOM_AMBIGUITY_HITS);
  const hasFreshTouchSection = mobileZoom.touchSection && t - mobileZoom.touchSectionAt <= MOBILE_ZOOM_TOUCH_GRACE_MS;
  if (!hasFreshTouchSection) return null;
  return ambiguousCandidates.find((section) => section.id === mobileZoom.touchSection.id) || null;
}

function viewportResizeHandler() {
  const viewport = window.visualViewport;
  if (!viewport || !Number.isFinite(viewport.scale)) return;

  const scale = viewport.scale;
  const previousScale = mobileZoom.lastScale;
  mobileZoom.lastScale = scale;
  if (previousScale === null || Math.abs(scale - previousScale) < MOBILE_ZOOM_SCALE_EPSILON) return;

  const t = Date.now();
  if (mobileZoom.gestureCounted) return;

  const section = selectMobileZoomSection(t);
  if (!section) return;

  const pageKey = ensureCurrentPage();
  const sec = getOrCreateSection(pageKey, section.id, section.sectionCategories);
  sec.zoom_count += 1;
  mobileZoom.gestureCounted = true;
}

export function syncViewportScale() {
  if (typeof window === 'undefined' || !window.visualViewport) return;
  const scale = window.visualViewport.scale;
  mobileZoom.lastScale = Number.isFinite(scale) ? scale : null;
}

let attached = false;

export function attach() {
  if (attached || typeof document === 'undefined') return;
  document.addEventListener('scroll', scrollHandler, { passive: true, capture: true });
  document.addEventListener('wheel', wheelHandler, { passive: true, capture: true });
  if (typeof window !== 'undefined' && window.visualViewport) {
    window.visualViewport.addEventListener('resize', viewportResizeHandler, { passive: true });
    syncViewportScale();
  }
  if (typeof TouchEvent !== 'undefined') {
    document.addEventListener('touchstart', touchStartHandler, { passive: true, capture: true });
    document.addEventListener('touchend', touchEndHandler, { passive: true, capture: true });
    document.addEventListener('touchcancel', touchEndHandler, { passive: true, capture: true });
  } else {
    document.addEventListener('pointerdown', pointerDownHandler, { passive: true, capture: true });
    document.addEventListener('pointerup', pointerUpHandler, { passive: true, capture: true });
    document.addEventListener('pointercancel', pointerUpHandler, { passive: true, capture: true });
  }
  attached = true;
}

export function detach() {
  if (!attached || typeof document === 'undefined') return;
  document.removeEventListener('scroll', scrollHandler, true);
  document.removeEventListener('wheel', wheelHandler, true);
  if (typeof window !== 'undefined' && window.visualViewport) {
    window.visualViewport.removeEventListener('resize', viewportResizeHandler);
  }
  if (typeof TouchEvent !== 'undefined') {
    document.removeEventListener('touchstart', touchStartHandler, true);
    document.removeEventListener('touchend', touchEndHandler, true);
    document.removeEventListener('touchcancel', touchEndHandler, true);
  } else {
    document.removeEventListener('pointerdown', pointerDownHandler, true);
    document.removeEventListener('pointerup', pointerUpHandler, true);
    document.removeEventListener('pointercancel', pointerUpHandler, true);
  }
  scrollLast = new WeakMap();
  zoomLast = new WeakMap();
  lastSessionScrollAt = 0;
  mobileZoom = createMobileZoomState();
  attached = false;
}
